import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { AppConfig } from "../AppConfig";
import { stockMenuApi } from "../core/http/httpClient";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions } from "../ducks/app";
import { Campaign } from "../ducks/campaign";
import { Category, Menu, menuActions, StockGroup } from "../ducks/menu";
import { Store, storeActions } from "../ducks/store";
import { getCurrentDateUTC } from "../utils/date";
import { useAuth } from "./useAuth";
import { CouponHook } from "./useCoupon";
import { useNavigation } from "./useNavigation";
import { useStore } from "./useStore";

export const useMenu = (
  useCoupon: CouponHook,
  storeCode?: string,
  date?: string,
) => {
  const dispatch = useDispatch();
  const { navigateToTopPage } = useNavigation();
  const { isLoggedIn } = useAuth();
  const { setFavoriteStore } = useStore();

  const [store, setStore] = useState<Store | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [favoriteMenus, setFavoriteMenus] = useState<Menu[]>([]);
  const [recommendedMenus, setRecommendedMenus] = useState<Menu[]>([]);
  const [limitedMenus, setLimitedMenus] = useState<Menu[]>([]);
  const [stockGroups, setStockGroups] = useState<StockGroup[]>([]); // 追加

  const [menuMapById, setMenuMapById] = useState<Map<number, Menu> | null>(
    null,
  );

  const { appliedCoupons, selectedAppCoupons } = useCoupon;

  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  useEffect(() => {
    if (!storeCode || !date) return;

    dispatch(appActions.showLoading());
    dispatch(
      storeActions.getStores.started({
        conditions: [
          {
            name: "店舗コード",
            operator: "equal",
            value: storeCode,
          },
        ],
        onError: () => dispatch(appActions.hideLoading()),
        onSuccess: (stores) => {
          if (stores.length === 0) {
            dispatch(
              appActions.showModal.started({
                title: "該当する店舗がありません",
                subTitle: "検索内容を変更してもう一度お試しください。",
                nextButtonLabel: "閉じる",
                handleNext: () => {
                  dispatch(appActions.closeModal());
                  navigateToTopPage();
                },
              }),
            );
            return;
          }

          setStore(stores[0]);
          // searchMenus(stores[0].id, date);
        },
      }),
    );
  }, []);

  const searchMenus = useCallback(
    (storeId: number, pickupDate: string) => {
      dispatch(
        menuActions.getMenus.started({
          store_id: storeId,
          pickup_date: pickupDate,
          nowTime: getCurrentDateUTC(),
          early_bird: !!campaign ? 1 : undefined,
          coupons: [
            ...(selectedAppCoupons
              ? selectedAppCoupons.map((coupon) => coupon.code)
              : []),
            ...(selectedAppCoupons && appliedCoupons
              ? appliedCoupons.filter(
                  (coupon) =>
                    !selectedAppCoupons.some((c) => c.code === coupon.code),
                )
              : (appliedCoupons ?? [])
            ).map((coupon) => coupon.code),
          ],
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: (categories) => {
            setCategories(categories);

            const favorites: Menu[] = [];
            const recommended: Menu[] = [];
            const limited: Menu[] = [];
            const menuMap = new Map<number, Menu>();
            categories.forEach((category) => {
              category.menus.forEach((menu) => {
                if (menu.isFavorite) favorites.push(menu);
                if (menu.isRecommended) recommended.push(menu);
                if (menu.isLimitedMenu) limited.push(menu);

                menuMap.set(menu.id, menu);
              });
            });
            setFavoriteMenus(favorites);
            setRecommendedMenus(
              recommended.sort((a, b) => a.recommendOrder! - b.recommendOrder!),
            );
            setLimitedMenus(limited);
            setMenuMapById(menuMap);
          },
        }),
      );
    },
    [selectedAppCoupons, appliedCoupons, campaign],
  );

  const categoriesToDisplay = useMemo(() => {
    if (favoriteMenus.length === 0) return categories;

    return [
      {
        id: -1,
        name: "お気に入り",
        sortOrder: -1,
        count: favoriteMenus.length,
        menus: favoriteMenus,
        text: "",
      },
      ...categories,
    ];
  }, [categories, favoriteMenus]);

  const setMultipleFavoriteMenus = useCallback(
    (menuCodes: string[], isFavorite: boolean, onSuccess?: () => void) => {
      if (!isLoggedIn) {
        return dispatch(
          appActions.showModal.started({
            title: "ログインしてください",
            nextButtonLabel: "閉じる",
            handleNext: () => dispatch(appActions.closeModal()),
          }),
        );
      }

      dispatch(
        menuActions.setFavoriteMenu.started({
          likes: menuCodes.map((menuCode) => ({
            menu_code: menuCode,
            enable: isFavorite,
          })),
          onSuccess: () => {
            const newFavoriteMenus: Menu[] = [];
            const newRecommendedMenus: Menu[] = [];
            const newLimitedMenus: Menu[] = [];
            const newCategories = categories.map((category) => {
              const newMenus = category.menus.map((menu) => {
                if (menu.isFavorite) newFavoriteMenus.push(menu);
                if (menu.isRecommended) newRecommendedMenus.push(menu);
                if (menu.isLimitedMenu) newLimitedMenus.push(menu);

                if (menuCodes.includes(menu.code)) {
                  if (isFavorite)
                    newFavoriteMenus.push({ ...menu, isFavorite });
                  else
                    newFavoriteMenus.splice(newFavoriteMenus.indexOf(menu), 1);

                  if (menu.isRecommended) {
                    newRecommendedMenus.pop();
                    newRecommendedMenus.push({ ...menu, isFavorite });
                  }

                  if (menu.isLimitedMenu) {
                    newLimitedMenus.pop();
                    newLimitedMenus.push({ ...menu, isFavorite });
                  }

                  return { ...menu, isFavorite };
                }
                return menu;
              });

              return { ...category, menus: newMenus };
            });

            setCategories(newCategories);
            setFavoriteMenus(newFavoriteMenus);
            setRecommendedMenus(
              newRecommendedMenus.sort(
                (a, b) => a.recommendOrder! - b.recommendOrder!,
              ),
            );
            setLimitedMenus(newLimitedMenus);
            onSuccess?.();
          },
        }),
      );
    },
    [categories, isLoggedIn],
  );

  const handleSetFavoriteStore = useCallback(
    (favorite: boolean, onSuccess?: () => void) => {
      if (!store) return;

      setFavoriteStore(store.id, favorite, () => {
        setStore({ ...store, isFavorite: favorite });
        onSuccess?.();
      });
    },
    [store],
  );

  const handleSetFavoriteMenu = useCallback(
    (
      menuId: number,
      menuCode: string,
      favorite: boolean,
      onSuccess?: () => void,
    ) => {
      if (!menuMapById) return;

      setMultipleFavoriteMenus([menuCode], favorite, () => {
        const menu = menuMapById.get(menuId);
        if (menu) {
          setMenuMapById(
            new Map(menuMapById.set(menuId, { ...menu, isFavorite: favorite })),
          );
        }

        onSuccess?.();
      });
    },
    [setMultipleFavoriteMenus, menuMapById],
  );

  // 在庫販売メニュー取得
  const searchStockMenus = useCallback(
    (storeId: number, campaignId: number, pickupDate: string) => {
      dispatch(appActions.showLoading());

      stockMenuApi
        .call({
          store_id: storeId,
          campaign_id: campaignId,
          pickup_date: pickupDate,
        })
        .then((response) => {
          const groups: StockGroup[] = response.data.groups.map((group) => {
            const userRemaining =
              group.user_ordered_qty !== null
                ? group.limit_qty_per_user - group.user_ordered_qty
                : group.limit_qty_per_user;
            const orderableQty = Math.min(group.stock_remaining, userRemaining);
            const isSoldOut = group.stock_remaining === 0;
            const isUserLimitReached =
              group.user_ordered_qty !== null &&
              group.user_ordered_qty >= group.limit_qty_per_user;

            const categories: Category[] = group.categories.map((cat) => ({
              id: cat.id,
              name: cat.name,
              text: cat.text,
              sortOrder: cat.sort_order,
              count: cat.count,
              menus: cat.menus.map((menu) => ({
                id: menu.id,
                name: menu.name,
                code: menu.code,
                categoryId: cat.id,
                imageUrl: menu.image,
                price: menu.price,
                priceWithTax: menu.price_with_tax,
                isFavorite: menu.favorite,
                isEnableButton: menu.enable_button && orderableQty > 0,
                isPreorder: menu.preorder,
                preorderMessage: menu.preorder_message,
                isLimitedMenu: false,
                isRecommended: menu.recommend !== null,
                recommendOrder: menu.recommend,
                details: menu.details ?? undefined,
                options: menu.option,
                campaign: null,
                isEarlyBird: false,
                stockGroupId: group.id,
                orderableQty: orderableQty,
              })),
            }));

            return {
              id: group.id,
              groupName: group.group_name,
              stockRemaining: group.stock_remaining,
              limitQtyPerUser: group.limit_qty_per_user,
              userOrderedQty: group.user_ordered_qty,
              orderableQty,
              isSoldOut,
              isUserLimitReached,
              categories,
            };
          });

          // menuMapByIdを構築（CartDetailPopupの表示に必要）
          const menuMap = new Map<number, Menu>();
          groups.forEach((group) => {
            group.categories.forEach((category) => {
              category.menus.forEach((menu) => {
                menuMap.set(menu.id, menu);
              });
            });
          });
          setMenuMapById(menuMap);
          setStockGroups(groups);
        })
        .catch(() => {})
        .finally(() => {
          dispatch(appActions.hideLoading());
        });
    },
    [dispatch],
  );

  return {
    store,
    menuMapById,
    recommendedMenus,
    limitedMenus,
    stockGroups, // 追加
    searchMenus,
    searchStockMenus, // 追加
    setCategories,
    categoriesToDisplay,
    setMultipleFavoriteMenus,
    favoriteMenus,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
  };
};
